import React, { useState } from 'react';
import { faTimesCircle, faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Modal, ModalSize } from '../../../components/modal/Modal';
import { createStyles } from 'utils/createStyle';
import { colors } from 'styles/settings';
import { unarchiveCardThunk } from 'common/features/store/duck/wallet/duck';
import { useRootSelector } from 'rootStore';
import { useDispatch } from 'react-redux';

interface UnArchiveCardModalProps {
    isOpen: boolean;
    closeModal: () => void;
    cardId: string;
    refreshArchivedCards: (() => void) | undefined;
}

const styles = createStyles({
    confirmButton: [
        'w-fit px-8 m-auto rounded-full bg-brand text-white font-semibold text-lg py-2 mb-2',
        { ':disabled': { backgroundColor: colors.greyAccent, color: colors.grey4 } },
    ],
});

function generateModalMetaData(submitSuccess: boolean): [string, string, string] {
    if (submitSuccess) {
        return [
            'This Card Has Been Unarchived',
            'You have successfully moved this card back to the ‘My Cards’ section of your wallet.',
            'Close',
        ];
    }

    return [
        'Unarchive Card',
        "Are you sure you want to move this card back to the 'My Cards' section of your Wallet?",
        'Cancel',
    ];
}

const UnArchiveCardModal = ({
    isOpen,
    closeModal,
    cardId,
    refreshArchivedCards,
}: UnArchiveCardModalProps) => {
    const [requestError, setRequestError] = useState('');
    const [loading, setLoading] = useState(false);
    const [unArchiveSuccess, setUnarchiveSuccess] = useState(false);
    const [title, caption, buttonText] = generateModalMetaData(unArchiveSuccess);
    const userId = useRootSelector((r) => r.store.account.userId) as string;
    const dispatch = useDispatch<any>();

    const onConfirmArchive = async (): Promise<void> => {
        setLoading(true);

        const res = await unarchiveCardThunk(userId, cardId)(dispatch);

        setLoading(false);

        if (res.error) {
            return setRequestError(
                'There was an issue unarchiving the card. Please try again later.'
            );
        }

        setUnarchiveSuccess(true);
        refreshArchivedCards && refreshArchivedCards();
    };

    const onCloseModal = () => {
        setUnarchiveSuccess(false);
        closeModal();
    };

    return (
        <Modal
            title=""
            isOpen={isOpen}
            onClose={onCloseModal}
            titleStyles={unArchiveSuccess ? 'text-success' : ''}
            size={[ModalSize.MEDIUM, ModalSize.MEDIUM]}
        >
            {requestError && (
                <label className="flex items-center justify-center text-error text-sm mb-4">
                    <FontAwesomeIcon icon={faTimesCircle} size="lg" className="mr-4" />
                    {requestError}
                </label>
            )}
            {unArchiveSuccess ? (
                <FontAwesomeIcon icon={faCheckCircle} className="text-success text-4xl mb-5" />
            ) : null}
            <p className="font-semibold text-29px text-center mb-4">{title}</p>
            <h5 className="font-normal mb-10 text-center text-grey-darkest text-[17px]">
                {caption}
            </h5>
            {!unArchiveSuccess && (
                <button
                    onClick={onConfirmArchive}
                    disabled={loading || Boolean(requestError.length)}
                    className={styles.confirmButton}
                >
                    {loading ? 'Unarchiving Card...' : 'Yes, Unarchive'}
                </button>
            )}
            <button
                className="text-brand font-semibold underline text-lg py-2"
                onClick={onCloseModal}
            >
                {buttonText}
            </button>
        </Modal>
    );
};

export default UnArchiveCardModal;
